import React, { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Mail, Phone, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMAIL = "mysmartdelegate@gmail.com";
const PHONE = "+40 784699771";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and a message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
      });
      setSent(true);
      toast.success("Message sent. I'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or email me directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <span className="section-label">Contact</span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-zinc-900 tracking-tight leading-[1.05]">
              Let&rsquo;s talk about your launch.
            </h2>
            <p className="mt-6 text-zinc-600 text-lg leading-relaxed max-w-md">
              Send a short note about your audience and what you&rsquo;d like to build. I&rsquo;ll reply personally within a couple of days.
            </p>

            <div className="mt-10 space-y-5">
              <a
                data-testid="contact-email-link"
                href={`mailto:${EMAIL}`}
                className="group flex items-start gap-4 p-5 rounded-2xl border border-zinc-200 hover:border-zinc-900 transition-colors duration-300"
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-900 text-white">
                  <Mail size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-400">Email</div>
                  <div className="font-heading text-base md:text-lg font-medium text-zinc-900 truncate">
                    {EMAIL}
                  </div>
                </div>
                <ArrowRight size={18} className="ml-auto text-zinc-400 group-hover:text-zinc-900 group-hover:translate-x-0.5 transition-all" />
              </a>

              <a
                data-testid="contact-phone-link"
                href={`tel:${PHONE.replace(/\s+/g, "")}`}
                className="group flex items-start gap-4 p-5 rounded-2xl border border-zinc-200 hover:border-zinc-900 transition-colors duration-300"
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-900 text-white">
                  <Phone size={18} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-400">Phone</div>
                  <div className="font-heading text-base md:text-lg font-medium text-zinc-900">
                    {PHONE}
                  </div>
                </div>
                <ArrowRight size={18} className="ml-auto text-zinc-400 group-hover:text-zinc-900 group-hover:translate-x-0.5 transition-all" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="rounded-3xl border border-zinc-200 bg-[#FAFAFA] p-7 md:p-10"
            >
              {sent ? (
                <div data-testid="contact-success" className="flex flex-col items-start gap-4 py-8">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                    <CheckCircle2 size={24} />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-zinc-900">Thanks &mdash; message received.</h3>
                  <p className="text-zinc-600 max-w-md">
                    I&rsquo;ll read it personally and reply within a couple of days. If it&rsquo;s urgent, you can also email me directly.
                  </p>
                  <Button
                    type="button"
                    data-testid="contact-send-another"
                    onClick={() => setSent(false)}
                    variant="ghost"
                    className="rounded-full mt-2"
                  >
                    Send another message
                  </Button>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Name" htmlFor="name">
                      <Input
                        id="name"
                        name="name"
                        data-testid="contact-input-name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Jane Creator"
                        required
                        className="h-12 rounded-xl bg-white border-zinc-200 focus-visible:ring-2 focus-visible:ring-blue-600"
                      />
                    </Field>
                    <Field label="Email" htmlFor="email">
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="contact-input-email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="you@domain.com"
                        required
                        className="h-12 rounded-xl bg-white border-zinc-200 focus-visible:ring-2 focus-visible:ring-blue-600"
                      />
                    </Field>
                  </div>

                  <Field label="Phone (optional)" htmlFor="phone">
                    <Input
                      id="phone"
                      name="phone"
                      data-testid="contact-input-phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+40 ..."
                      className="h-12 rounded-xl bg-white border-zinc-200 focus-visible:ring-2 focus-visible:ring-blue-600"
                    />
                  </Field>

                  <Field label="Tell me about your audience &amp; idea" htmlFor="message">
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Where do you create, audience size, what you'd love to launch..."
                      required
                      rows={6}
                      className="rounded-xl bg-white border-zinc-200 focus-visible:ring-2 focus-visible:ring-blue-600 resize-none"
                    />
                  </Field>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                    <p className="text-xs text-zinc-500">
                      I&rsquo;ll only use this to reply to you. No marketing, no spam.
                    </p>
                    <Button
                      type="submit"
                      data-testid="contact-submit-button"
                      disabled={loading}
                      className="rounded-full bg-zinc-900 hover:bg-blue-700 text-white h-12 px-7 text-base font-medium transition-all duration-300 active:scale-[0.98] disabled:opacity-60"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={16} className="mr-2 animate-spin" /> Sending...
                        </>
                      ) : (
                        <>
                          Send message
                          <ArrowRight size={16} className="ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, htmlFor, children }) => (
  <div className="space-y-2">
    <Label htmlFor={htmlFor} className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
      <span dangerouslySetInnerHTML={{ __html: label }} />
    </Label>
    {children}
  </div>
);

export default Contact;
